// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type StoreMapCoverage = components["schemas"]["StoreMapCoverageResponse"];
export type StoreMapCoveragePoint = components["schemas"]["StoreMapCoveragePointResponse"];
export type StoreMapCoverageThreshold = components["schemas"]["StoreMapCoverageThresholdResponse"];
export type StoreMapCoverageQuality = components["schemas"]["StoreMapCoverageQualityEnum"];

// Whole days of history before today. 1 means yesterday, and uptime deliberately
// stops there: schedule rows exist for slots that have not happened yet, so
// counting a day still in progress reads its remaining slots as misses and drags
// every camera down. The cost is that a fix made this morning shows up tomorrow.
export const COVERAGE_WINDOW_DAYS = 1;

// The scan aggregates every signal reading in the store over the window, so it
// costs seconds, not milliseconds. Held long enough that panning the map or
// leaving and re-entering the tab never pays for it twice.
const COVERAGE_STALE_TIME = 300_000;

/**
 * WiFi coverage points for the store map overlay.
 *
 * Disabled until explicitly refetched: this is heavy enough that it must be a
 * deliberate click, never something that fires because a tab mounted.
 */
export function useStoreMapCoverageQuery(storeName: string, days: number = COVERAGE_WINDOW_DAYS) {
  return useQuery({
    queryKey: ["stores", "map-coverage", storeName, days],
    staleTime: COVERAGE_STALE_TIME,
    enabled: false,
    queryFn: async () => {
      const res = await api.GET("/v1/stores/{store_name}/map/coverage", {
        params: { path: { store_name: storeName }, query: { days } },
      });
      // An error status with an empty body leaves BOTH `error` and `data`
      // undefined, so checking `error` alone would resolve the query to
      // undefined and the overlay would then read `.points` off it.
      if (res.error || !res.data) throw new Error("Failed to load WiFi coverage");
      return res.data as StoreMapCoverage;
    },
  });
}
